import { useEffect, useState } from "react";
import { ArrowUp, ArrowDown, Camera, Plus, Trash2, RotateCcw, Thermometer } from "lucide-react";
import { toast } from "sonner";
import { api, fmtErr } from "../lib/api";
import { Button } from "./ui/button";

const slug = (s) => s.toLowerCase().replace(/[^a-z0-9]+/g, "-");
const swap = (a, i, d) => { const b = [...a]; [b[i], b[i + d]] = [b[i + d], b[i]]; return b; };
const iconBtn = "p-1 disabled:opacity-30";

const ItemEditor = ({ it, i, n, tid, canEdit, onChange, onMove, onDelete }) => {
  const reading = it.unit !== undefined;
  return (
    <div className="space-y-1.5" data-testid={`${tid}-row`}>
      <div className="flex items-center gap-1.5">
        <input className="field h-10" disabled={!canEdit} value={it.label} placeholder="Question" onChange={(e) => onChange({ label: e.target.value })} data-testid={`${tid}-text`} />
        <button type="button" disabled={!canEdit} title="Photo always required" onClick={() => onChange({ photo: !it.photo })} data-testid={`${tid}-photo`}
          className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-md border transition-colors ${it.photo ? "border-amber-400 bg-amber-500/20 text-amber-200" : "border-slate-700 text-slate-500"}`}><Camera className="h-4 w-4" /></button>
        {canEdit && (
          <>
            <button type="button" title="Temperature / reading with limits" onClick={() => onChange(reading ? { unit: undefined, min: undefined, max: undefined } : { unit: "°C", min: "", max: "" })} data-testid={`${tid}-reading-toggle`}
              className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-md border transition-colors ${reading ? "border-sky-400 bg-sky-500/20 text-sky-200" : "border-slate-700 text-slate-500"}`}><Thermometer className="h-4 w-4" /></button>
            <button type="button" disabled={i === 0} onClick={() => onMove(-1)} className={iconBtn} data-testid={`${tid}-up`}><ArrowUp className="h-4 w-4" /></button>
            <button type="button" disabled={i === n - 1} onClick={() => onMove(1)} className={iconBtn} data-testid={`${tid}-down`}><ArrowDown className="h-4 w-4" /></button>
            <button type="button" onClick={onDelete} className="p-1 text-red-300" data-testid={`${tid}-delete`}><Trash2 className="h-4 w-4" /></button>
          </>
        )}
      </div>
      {reading && (
        <div className="flex flex-wrap items-center gap-2 pl-1 text-xs text-slate-400">
          <span>Reading in</span>
          <input className="field h-8 w-16" disabled={!canEdit} value={it.unit} onChange={(e) => onChange({ unit: e.target.value })} data-testid={`${tid}-unit`} />
          <span>min</span>
          <input type="number" className="field h-8 w-20" disabled={!canEdit} value={it.min ?? ""} onChange={(e) => onChange({ min: e.target.value })} data-testid={`${tid}-min`} />
          <span>max</span>
          <input type="number" className="field h-8 w-20" disabled={!canEdit} value={it.max ?? ""} onChange={(e) => onChange({ max: e.target.value })} data-testid={`${tid}-max`} />
        </div>
      )}
    </div>
  );
};

const SectionEditor = ({ sec, si, n, tid, canEdit, onChange, onMove, onDelete }) => {
  const items = sec.items;
  const setItems = (next) => onChange({ ...sec, items: next });
  return (
    <div className="space-y-2 rounded-md border border-slate-800 p-3" data-testid={tid}>
      <div className="flex items-center gap-1.5">
        <input className="field h-10 font-semibold uppercase text-rose-300" disabled={!canEdit} value={sec.title} placeholder="Section title" onChange={(e) => onChange({ ...sec, title: e.target.value })} data-testid={`${tid}-title`} />
        {canEdit && (
          <>
            <button type="button" disabled={si === 0} onClick={() => onMove(-1)} className={iconBtn} data-testid={`${tid}-up`}><ArrowUp className="h-4 w-4" /></button>
            <button type="button" disabled={si === n - 1} onClick={() => onMove(1)} className={iconBtn} data-testid={`${tid}-down`}><ArrowDown className="h-4 w-4" /></button>
            <button type="button" onClick={onDelete} className="p-1 text-red-300" data-testid={`${tid}-delete`}><Trash2 className="h-4 w-4" /></button>
          </>
        )}
      </div>
      {items.map((it, i) => (
        <ItemEditor key={i} it={it} i={i} n={items.length} tid={`${tid}-q-${i}`} canEdit={canEdit}
          onChange={(p) => setItems(items.map((x, j) => (j === i ? { ...x, ...p } : x)))}
          onMove={(d) => setItems(swap(items, i, d))} onDelete={() => setItems(items.filter((_, j) => j !== i))} />
      ))}
      {canEdit && <button type="button" onClick={() => setItems([...items, { label: "", photo: false }])} className="flex items-center gap-1 text-xs text-slate-300 hover:text-slate-100" data-testid={`${tid}-add-question`}><Plus className="h-3.5 w-3.5" />Add question</button>}
    </div>
  );
};

const validate = (sections) => {
  if (sections.some((s) => s.items.some((i) => !i.label.trim()))) return "Fill in or delete the blank question first";
  if (sections.some((s) => !s.title.trim())) return "Every section needs a title";
  if (!sections.some((s) => s.items.length)) return "Add at least one question";
  return null;
};

export const ChecklistEditor = ({ tkey, tpl, canEdit, onSaved }) => {
  const [sections, setSections] = useState(tpl.sections);
  const [open, setOpen] = useState(false);
  const tid = `cl-${slug(tkey)}`;
  useEffect(() => { setSections(tpl.sections); }, [tpl]);
  const all = tpl.sections.flatMap((s) => s.items);
  const save = async () => {
    const err = validate(sections);
    if (err) return toast.error(err);
    try {
      await api.put(`/checklists/${tkey}`, { sections });
      toast.success(`${tpl.name} saved`);
      onSaved();
    } catch (e) { toast.error(fmtErr(e)); }
  };
  const reset = async () => {
    if (!window.confirm(`Reset ${tpl.name} to the standard questions?`)) return;
    try {
      await api.delete(`/checklists/${tkey}`);
      toast.success("Reset to standard questions");
      onSaved();
    } catch (e) { toast.error(fmtErr(e)); }
  };
  return (
    <div className="panel" data-testid={tid}>
      <button type="button" onClick={() => setOpen(!open)} className="flex w-full items-center justify-between gap-3 px-4 py-3 text-left" data-testid={`${tid}-toggle`}>
        <span>
          <span className="font-semibold">{tpl.name}</span>
          <span className="block text-xs text-slate-400" data-testid={`${tid}-summary`}>{all.length} questions - {all.filter((q) => q.photo).length} need a photo - every {tpl.frequency_days} day(s){tpl.customised ? " - customised" : ""}</span>
        </span>
        <span className="eyebrow text-[10px]">{open ? "Close" : canEdit ? "Edit" : "View"}</span>
      </button>
      {open && (
        <div className="space-y-3 border-t border-slate-800 p-4">
          {sections.map((s, si) => (
            <SectionEditor key={si} sec={s} si={si} n={sections.length} tid={`${tid}-s-${si}`} canEdit={canEdit}
              onChange={(v) => setSections(sections.map((x, j) => (j === si ? v : x)))}
              onMove={(d) => setSections(swap(sections, si, d))} onDelete={() => setSections(sections.filter((_, j) => j !== si))} />
          ))}
          {canEdit && (
            <div className="flex flex-wrap gap-2 pt-1">
              <Button type="button" variant="outline" className="border-slate-700 bg-transparent" onClick={() => setSections([...sections, { title: "", items: [{ label: "", photo: false }] }])} data-testid={`${tid}-add-section`}><Plus className="mr-1 h-4 w-4" />Add section</Button>
              <Button type="button" className="bg-rose-600 hover:bg-rose-700" onClick={save} data-testid={`${tid}-save`}>Save</Button>
              {tpl.customised && <Button type="button" variant="ghost" onClick={reset} data-testid={`${tid}-reset`}><RotateCcw className="mr-1 h-4 w-4" />Reset to standard</Button>}
            </div>
          )}
        </div>
      )}
    </div>
  );
};
